import React, { useState, useRef, memo, useCallback } from 'react';
import { Show, useRecordContext } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Tabs,
  Tab as MuiTab
} from '@mui/material';

import { S3ImageField } from '../fields';
import { VARIANT_FIELDS } from './constants';
import { ShowField, LazyTabPanel, DirectoryShowContent } from './components';

const SummaryTab = memo(() => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card variant="outlined">
          <CardContent>
            <ShowField label="Name">{record.name}</ShowField>
            <ShowField label="Version">{record.version || '—'}</ShowField>
            <ShowField label="Type">
              <Chip label={record.type} size="small" color="primary" variant="outlined" sx={{ textTransform: 'capitalize' }} />
            </ShowField>
            <ShowField label="Hotspots">
              <Chip label={record.hasHotspots ? 'Yes' : 'No'} size="small" color={record.hasHotspots ? 'success' : 'default'} variant="outlined" />
            </ShowField>
            <Box display="flex" gap={1} mt={1}>
              <Chip label={record.disabled ? 'Disabled' : 'Active'} size="small" color={record.disabled ? 'error' : 'success'} />
              {record.featured && <Chip label="Featured" size="small" color="info" />}
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card variant="outlined">
          <CardContent>
            <ShowField label="Thumbnail">
              <S3ImageField source="thumbnail" />
            </ShowField>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
});

const ProductionTab = memo(() => (
  <DirectoryShowContent {...VARIANT_FIELDS.production} />
));

const BetaTab = memo(() => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <ShowField label="Beta Enabled">
            <Chip
              label={record.betaEnabled ? 'Enabled' : 'Disabled'}
              size="small"
              color={record.betaEnabled ? 'success' : 'default'}
              variant={record.betaEnabled ? 'filled' : 'outlined'}
            />
          </ShowField>
        </CardContent>
      </Card>
      {record.betaEnabled && (
        <DirectoryShowContent {...VARIANT_FIELDS.beta} />
      )}
    </>
  );
});

const TABS = ['Summary', 'Production', 'Beta'];

const TemplateShow = props => {
  const [tab, setTab] = useState(0);
  const visitedRef = useRef(new Set([0]));
  const handleTabChange = useCallback((e, v) => {
    visitedRef.current.add(v);
    setTab(v);
  }, []);

  return (
    <Show {...props}>
      <Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 },
              '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' }
            }}
          >
            {TABS.map(label => <MuiTab key={label} label={label} />)}
          </Tabs>
        </Box>
        <LazyTabPanel active={tab === 0} visited={visitedRef.current.has(0)}>
          <SummaryTab />
        </LazyTabPanel>
        <LazyTabPanel active={tab === 1} visited={visitedRef.current.has(1)}>
          <ProductionTab />
        </LazyTabPanel>
        <LazyTabPanel active={tab === 2} visited={visitedRef.current.has(2)}>
          <BetaTab />
        </LazyTabPanel>
      </Box>
    </Show>
  );
};

export default TemplateShow;
